import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const campaignPrizeOrdersSlice = createSlice({
    name: "campaignPrizeOrders",
    initialState: {
        page: 0,
        pageCount: 0,
        success: false,
        fail: false,
        listData: null,
        detailData: null,
        statsData: null,
        errorDetailData: null,
    },
    reducers: {
        doGetCampaignPrizeOrdersListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload?.pagination?.page;
            state.pageCount = action.payload?.pagination?.totalPages;
        },
        doGetCampaignPrizeOrdersListFail: (state, action) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.errorDetailData = action.payload;
        },
        doGetCampaignPrizeOrdersDetailSuccess: (state, action) => {
            state.detailData = action.payload.data;
        },
        doGetCampaignPrizeOrdersDetailFail: (state, action) => {
            state.detailData = null;
            state.errorDetailData = action.payload;
        },
        doGetCampaignPrizeOrdersStatsSuccess: (state, action) => {
            state.statsData = action.payload.data;
        },
        doGetCampaignPrizeOrdersStatsFail: (state, action) => {
            state.statsData = null;
            state.errorDetailData = action.payload;
        },
        doUpdateCampaignPrizeOrderStatusSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
        },
        doUpdateCampaignPrizeOrderStatusFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        doUpdateCampaignPrizeOrderShippingSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
        },
        doUpdateCampaignPrizeOrderShippingFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        resetCampaignPrizeOrdersStateSuccess: (state, action) => {
            state.success = false;
            state.fail = false;
            state.detailData = null;
            state.errorDetailData = null;
        },
    },
});

/**
 * Get campaign prize orders list with pagination and filters
 */
export const doGetCampaignPrizeOrdersList = (param) => async (dispatch) => {
    try {
        let url = `/api/admin/campaign-prize-orders?page=${param.page}&size=${param.limit || 20}`;

        if (param.status) {
            url += `&status=${encodeURIComponent(param.status)}`;
        }
        if (param.prize_type) {
            url += `&prize_type=${encodeURIComponent(param.prize_type)}`;
        }
        if (param.vip_level) {
            url += `&vip_level=${encodeURIComponent(param.vip_level)}`;
        }
        if (param.search) {
            url += `&search=${encodeURIComponent(param.search)}`;
        }

        const response = await axios.get(url);
        dispatch(doGetCampaignPrizeOrdersListSuccess(response.data));
    } catch (error) {
        dispatch(doGetCampaignPrizeOrdersListFail(error.response));
    }
};

/**
 * Get single campaign prize order details
 */
export const doGetCampaignPrizeOrdersDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/campaign-prize-orders/${param.id}`;
        const response = await axios.get(url);
        dispatch(doGetCampaignPrizeOrdersDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetCampaignPrizeOrdersDetailFail(error.response));
    }
};

/**
 * Get campaign prize orders statistics
 */
export const doGetCampaignPrizeOrdersStats = () => async (dispatch) => {
    try {
        const url = `/api/admin/campaign-prize-orders/stats`;
        const response = await axios.get(url);
        dispatch(doGetCampaignPrizeOrdersStatsSuccess(response.data));
    } catch (error) {
        dispatch(doGetCampaignPrizeOrdersStatsFail(error.response));
    }
};

/**
 * Update campaign prize order status
 */
export const doUpdateCampaignPrizeOrderStatus = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/campaign-prize-orders/${param.id}/status`;
        const response = await axios.patch(url, {
            order_status: param.order_status,
            notes: param.notes,
        });
        dispatch(doUpdateCampaignPrizeOrderStatusSuccess(response.data));
        return { success: true };
    } catch (error) {
        dispatch(doUpdateCampaignPrizeOrderStatusFail(error.response));
        return { success: false, error: error.response?.data?.message };
    }
};

/**
 * Update campaign prize order shipping info
 */
export const doUpdateCampaignPrizeOrderShipping = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/campaign-prize-orders/${param.id}/shipping`;
        const response = await axios.patch(url, {
            tracking_number: param.tracking_number,
            shipping_carrier: param.shipping_carrier,
            shipping_name: param.shipping_name,
            shipping_address: param.shipping_address,
            shipping_phone: param.shipping_phone,
            order_status: param.order_status,
        });
        dispatch(doUpdateCampaignPrizeOrderShippingSuccess(response.data));
        return { success: true };
    } catch (error) {
        dispatch(doUpdateCampaignPrizeOrderShippingFail(error.response));
        return { success: false, error: error.response?.data?.message };
    }
};

/**
 * Reset campaign prize orders state
 */
export const resetCampaignPrizeOrdersState = () => async (dispatch) => {
    dispatch(resetCampaignPrizeOrdersStateSuccess(false));
};

// Export actions
export const {
    doGetCampaignPrizeOrdersListSuccess,
    doGetCampaignPrizeOrdersListFail,
    doGetCampaignPrizeOrdersDetailSuccess,
    doGetCampaignPrizeOrdersDetailFail,
    doGetCampaignPrizeOrdersStatsSuccess,
    doGetCampaignPrizeOrdersStatsFail,
    doUpdateCampaignPrizeOrderStatusSuccess,
    doUpdateCampaignPrizeOrderStatusFail,
    doUpdateCampaignPrizeOrderShippingSuccess,
    doUpdateCampaignPrizeOrderShippingFail,
    resetCampaignPrizeOrdersStateSuccess,
} = campaignPrizeOrdersSlice.actions;

// Export selectors
export const getCampaignPrizeOrdersList = (state) => state.campaignPrizeOrders?.listData;
export const getCampaignPrizeOrdersListPage = (state) => state.campaignPrizeOrders?.page;
export const getCampaignPrizeOrdersListPageCount = (state) => state.campaignPrizeOrders?.pageCount;
export const getCampaignPrizeOrdersDetail = (state) => state.campaignPrizeOrders?.detailData;
export const getCampaignPrizeOrdersStats = (state) => state.campaignPrizeOrders?.statsData;
export const getCampaignPrizeOrdersErrorDetailData = (state) => state.campaignPrizeOrders?.errorDetailData;
export const getCampaignPrizeOrdersSuccess = (state) => state.campaignPrizeOrders?.success;
export const getCampaignPrizeOrdersFail = (state) => state.campaignPrizeOrders?.fail;

export default campaignPrizeOrdersSlice.reducer;
